import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useDirtyForm } from '../../hooks/useDirtyForm';
import { useToast } from '../shared/Toast';
import { LoadFailed } from '../settings/instantSettings';
import { SettingsSheet, SheetBlock } from '../settings/sheet';
import { StoreLoadState } from '../settings/storeSettingsForm';
import UnsavedChangesBar from '../settings/UnsavedChangesBar';
import AgentSection from './AgentSection';
import CategoryRoutingSection from './CategoryRoutingSection';
import LookSection from './LookSection';
import PrintersSection from './PrintersSection';
import RoutingSection from './RoutingSection';
import { ApiResult, PrintingAgent, PrintingCategory, PrintingPrinter, PrintingZone, RoutingRule, printingApi } from './printingApi';
import {
  EMPTY_FORM, LOOK_KEYS, NO_ORDER_TARGETS, OrderTargetsInfo, PageForm, ROUTING_KEYS, STATUS_REFRESH_MS, SWITCH_KEYS,
  TICKET_OPTION_KEYS, categoryRows, formFrom, keepPrinters, lookBody, lookLinesOk, mainReceiptPrinter, orderTargetsInfo,
  receiptPrinters, routingBody, switchesBody, ticketOptionsBody, ticketPrinters,
} from './printingModel';

/**
 * Ταμείο → «Εκτυπωτές» (απλοποίηση 14/10/2026, ξανά 25/10/2026 για τους τεχνικούς): ένα «Φύλλο» με
 * ενότητες — Υπολογιστής ταμείου, Εκτυπωτές, Πού βγαίνει κάθε χαρτί, Δελτία ανά κατηγορία, Πώς βγαίνει
 * κάθε χαρτί (προεπισκόπηση με swipe + ρυθμίσεις). Οι «Επιδόσεις εκτύπωσης» είναι από 25/10/2026 στην κάρτα
 * καταστήματος του support, όχι εδώ (δεν λένε τίποτα στον ιδιοκτήτη). Όλα από ένα GET /printing· agent και
 * εκτυπωτές ξαναδιαβάζονται κάθε 30″ όσο φαίνεται η σελίδα (κρυφή καρτέλα → καμία κλήση).
 * Εκτυπωτές/κλειδί γράφονται αμέσως· δρομολόγηση, διακόπτες, απόδειξη και επιλογές δελτίων με ΜΙΑ μπάρα
 * αλλαγών που στέλνει μόνο ό,τι άλλαξε. Καμία καρτέλα «Templates»: μία σταθερή μορφή απόδειξης.
 */

type Status = 'loading' | 'ready' | 'error';

const visible = (): boolean => typeof document === 'undefined' || document.visibilityState !== 'hidden';

const PrintingPage: React.FC = () => {
  const { t } = useTranslation(['printing', 'settings']);
  const { store } = useAuth();
  const { showToast } = useToast();
  const storeId = store?.id;
  const [status, setStatus] = useState<Status>('loading');
  // 403 = όχι διαχειριστής· οτιδήποτε άλλο = γενικό μήνυμα (όχι το αγγλικό κείμενο ενός 500).
  const [forbidden, setForbidden] = useState(false);
  const [agent, setAgent] = useState<PrintingAgent | null>(null);
  const [printers, setPrinters] = useState<PrintingPrinter[]>([]);
  const [categories, setCategories] = useState<PrintingCategory[]>([]);
  const [zones, setZones] = useState<PrintingZone[]>([]);
  const rows = useMemo(() => categoryRows(categories), [categories]);
  const passthrough = useRef<RoutingRule[]>([]);
  // Από το τελευταίο πλήρες GET: αν ο server ξέρει τους στόχους Take Away/Τραπέζια/Παραστατικά
  // delivery (274) και ποιοι αποθηκευμένοι δεν ισχύουν πια — δεν είναι τιμές της φόρμας.
  const [orderTargets, setOrderTargets] = useState<OrderTargetsInfo>(NO_ORDER_TARGETS);
  const form = useDirtyForm<PageForm>(EMPTY_FORM, { guardId: 'printing' });
  const { reset, setValues } = form;
  const [saving, setSaving] = useState(false);

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);
  // Αύξων αριθμός αιτήματος: agent/εκτυπωτές γράφει μόνο απάντηση νεότερη από ό,τι ήδη φαίνεται,
  // τη φόρμα μόνο το νεότερο «full» (μια αργή απάντηση δεν γυρίζει πίσω την οθόνη).
  const seq = useRef(0);
  const appliedSeq = useRef(0);
  const fullSeq = useRef(0);
  const inFlight = useRef(0);
  const lastLoad = useRef(0);
  const dirtyRef = useRef(form.dirty);
  dirtyRef.current = form.dirty;

  /**
   * full: όλη η σελίδα (πρώτη φόρτωση, μετά από αλλαγή εκτυπωτή)· η φόρμα ξαναστήνεται ΜΟΝΟ αν δεν έχει
   * αλλαγές. status: μόνο agent και εκτυπωτές (ο κύκλος των 30″) — ποτέ η φόρμα.
   */
  const load = useCallback(async (mode: 'full' | 'status'): Promise<PrintingPrinter[] | null> => {
    if (!storeId) return null;
    const id = ++seq.current;
    if (mode === 'full') fullSeq.current = id;
    inFlight.current += 1;
    lastLoad.current = Date.now();
    const r = await printingApi.load(storeId);
    inFlight.current -= 1;
    if (!alive.current) return null;
    const latestFull = mode === 'full' && id === fullSeq.current;
    if (!r.ok || !r.data) {
      if (latestFull) {
        setForbidden(r.status === 403);
        setStatus(prev => (prev === 'ready' ? prev : 'error'));
      }
      return null;
    }
    if (id > appliedSeq.current) {
      appliedSeq.current = id;
      setAgent(r.data.agent);
      setPrinters(r.data.printers);
    }
    if (latestFull) {
      setCategories(r.data.categories);
      setZones(r.data.routing.zones);
      setOrderTargets(orderTargetsInfo(r.data.routing));
      const next = formFrom(r.data, categoryRows(r.data.categories));
      passthrough.current = next.passthrough;
      if (!dirtyRef.current) reset(next.form);
      setStatus('ready');
    }
    return r.data.printers;
  }, [storeId, reset]);

  useEffect(() => {
    setStatus('loading');
    load('full');
  }, [load]);

  // Κάθε 30″ όσο φαίνεται· στην επιστροφή σε ορατή καρτέλα αμέσως, αν πέρασε ο χρόνος.
  useEffect(() => {
    if (!storeId || status !== 'ready') return undefined;
    const tick = () => {
      if (!visible() || inFlight.current > 0) return;
      load('status');
    };
    const timer = setInterval(tick, STATUS_REFRESH_MS);
    const onVisibility = () => {
      if (visible() && Date.now() - lastLoad.current >= STATUS_REFRESH_MS) tick();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [storeId, status, load]);

  const retry = () => {
    setStatus('loading');
    load('full');
  };

  // Διαγραφή ή εκτυπωτής χωρίς «Δελτία»/«Αποδείξεις»: ο server σβήνει τους κανόνες/στόχους του (και θα
  // απέρριπτε το PUT τους). Φόρμα χωρίς αλλαγές ξαναστήνεται από το GET· με αλλαγές κρατά μόνο ενεργούς
  // εκτυπωτές δελτίων στα δελτία και ενεργούς εκτυπωτές αποδείξεων στα παραστατικά delivery.
  const afterPrinterChange = async () => {
    const fresh = await load('full');
    if (fresh && dirtyRef.current) {
      const allowed = {
        tickets: new Set(ticketPrinters(fresh).map(p => p.id)),
        receipts: new Set(receiptPrinters(fresh).map(p => p.id)),
      };
      const main = mainReceiptPrinter(fresh);
      setValues(prev => keepPrinters(prev, allowed, main ? String(main.id) : ''));
    }
  };

  const save = async () => {
    if (!storeId || saving || !form.dirty) return;
    const values = form.values;
    const changed = form.changedKeys;
    const parts: Array<{ keys: Array<keyof PageForm>; run: () => Promise<ApiResult<unknown>> }> = [];
    if (changed.some(k => ROUTING_KEYS.includes(k))) {
      const body = routingBody(rows, values, passthrough.current, form.saved);
      parts.push({ keys: ROUTING_KEYS, run: () => printingApi.saveRouting(storeId, body) });
    }
    const switches = switchesBody(changed, values);
    if (Object.keys(switches).length > 0) parts.push({ keys: SWITCH_KEYS, run: () => printingApi.saveSwitches(storeId, switches) });
    const look = lookBody(changed, values);
    if (Object.keys(look).length > 0) parts.push({ keys: LOOK_KEYS, run: () => printingApi.saveReceiptLook(storeId, look) });
    const ticketOptions = ticketOptionsBody(changed, values);
    if (Object.keys(ticketOptions).length > 0) {
      parts.push({ keys: TICKET_OPTION_KEYS, run: () => printingApi.saveTicketOptions(storeId, ticketOptions) });
    }

    setSaving(true);
    const done: Array<keyof PageForm> = [];
    let failure: ApiResult<unknown> | null = null;
    for (const part of parts) {
      // Ένα-ένα: αν αποτύχει ένα κομμάτι, τα υπόλοιπα σώζονται και η μπάρα μένει μόνο γι' αυτό.
      // eslint-disable-next-line no-await-in-loop
      const r = await part.run();
      if (r.ok) done.push(...part.keys);
      else if (!failure) failure = r;
    }
    if (!alive.current) return;
    setSaving(false);
    const nextSaved = { ...form.saved };
    done.forEach(k => { (nextSaved as Record<string, unknown>)[k] = values[k]; });
    reset(nextSaved);
    // Το PUT routing γράφει ΟΛΟΥΣ τους στόχους (ο server ελέγχει κάθε τιμή): ό,τι δεν ίσχυε πια
    // καθαρίστηκε, άρα οι παλιές προειδοποιήσεις του GET δεν αληθεύουν. Νέος κύριος εκτυπωτής
    // αποδείξεων = άλλαξε και η λίστα των εκτυπωτών («Κύριος» στη γραμμή του): ξαναδιαβάζεται.
    if (done.includes('rules')) {
      setOrderTargets(prev => ({ ...prev, inactive: [] }));
      if (values.mainReceipt !== form.saved.mainReceipt) load('status');
    }
    if (failure) {
      setValues(() => values);
      const reason = failure.status === 403
        ? t('printing:forbidden')
        : failure.status === 0 ? t('printing:form.network') : failure.error;
      showToast('error', reason ? t('printing:saveFailedReason', { error: reason }) : t('printing:saveFailed'));
    } else {
      showToast('success', t('printing:saved'));
    }
  };

  if (!storeId) return null;
  const takeaway = store?.takeaway === true;
  const dineIn = store?.dine_in === true;

  return (
    <SettingsSheet eyebrow={t('settings:nav.groups.register')} title={t('printing:title')} subtitle={t('printing:subtitle')}>
      {status === 'loading' || (status === 'ready' && !agent) ? (
        <SheetBlock><StoreLoadState status="loading" onRetry={retry} /></SheetBlock>
      ) : status === 'error' ? (
        <SheetBlock><LoadFailed message={forbidden ? t('printing:forbidden') : t('printing:loadFailed')} onRetry={retry} /></SheetBlock>
      ) : (
        <>
          <AgentSection storeId={storeId} agent={agent!} onChanged={() => { load('status'); }} />
          <PrintersSection storeId={storeId} printers={printers} agent={agent!} onChanged={afterPrinterChange} />
          <fieldset disabled={saving} className="min-w-0">
            <RoutingSection printers={printers} zones={zones} form={form} takeaway={takeaway} dineIn={dineIn} orderTargets={orderTargets} />
            <CategoryRoutingSection rows={rows} printers={printers} form={form} passthrough={passthrough.current}
              takeaway={takeaway} dineIn={dineIn} orderTargets={orderTargets} />
            <LookSection storeId={storeId} form={form} printers={printers} takeaway={takeaway} dineIn={dineIn} />
          </fieldset>
        </>
      )}

      <UnsavedChangesBar dirty={form.dirty} saving={saving} onSave={save} onReset={() => form.reset()}
        canSave={lookLinesOk(form.values)} />
    </SettingsSheet>
  );
};

export default PrintingPage;
